import type { SolutionPremiumSections, SolutionSubpageSlug } from './types';
import { partnerDemoCta, sharedTrustedLogos } from './premiumShared';

const trustedBy = {
  title: 'Trusted by partners worldwide',
  logos: sharedTrustedLogos,
} as const;

export const solutionPremiumSections: Record<SolutionSubpageSlug, SolutionPremiumSections> = {
  'smart-manufacturing': {
    heroEyebrow: 'Industrial Internet Of Things',
    trustedBy,
    education: {
      tabs: [
        {
          id: 'what',
          label: 'What',
          title: 'What is smart product manufacturing?',
          items: [],
          blocks: [
            {
              title: 'What is an IIoT solution?',
              description:
                'Industrial IoT (IIoT) solutions monitor, collect, exchange, and analyze real-time sensor data from your equipment. They deliver meaningful shop-floor insights and advanced analytics.',
            },
            {
              title: 'What is Industry 4.0?',
              description:
                'Industry 4.0 refers to the integration of advanced technologies, like IoT, AI, and robotics, for enhanced manufacturing automation and industrial process connectivity.',
            },
          ],
          cta: { label: 'More FAQs', to: '#faqs' },
        },
        {
          id: 'why',
          label: 'Why',
          title: 'Why manufacturers choose Aeviot',
          items: [
            'Ship connected products in as little as 3 months.',
            'Save 70–80% versus building firmware, cloud, and apps from scratch.',
            'Own the brand experience end-to-end with white-label tooling.',
            'Gain real-time product analytics and OTA update capabilities.',
          ],
        },
        {
          id: 'how',
          label: 'How',
          title: 'How to get started',
          items: [
            'Submit a partnership request with your product details.',
            'Our team assesses hardware compatibility and integration path.',
            'We configure your branded platform, apps, and firmware SDK.',
            'Launch production with QA, documentation, and ongoing support.',
          ],
          cta: partnerDemoCta,
        },
      ],
    },
    verticals: {
      eyebrow: 'INDUSTRIES',
      title: 'Intelligent shop floor automation',
      subtitle:
        'Industry 4.0 is just a click away. Whether you want to monitor one machine or automate your entire factory, we have you covered.',
      sceneImage: {
        src: '/images/solutions/smart-manufacturing/industries-image.png',
        alt: 'Discrete and process manufacturing streams converging into smart manufacturing intelligence',
        width: 1400,
        height: 820,
      },
      convergence: {
        title: 'Smart Manufacturing Intelligence',
        subtitle:
          'Where discrete and process operations connect through automation, analytics, and real-time factory visibility.',
      },
      items: [
        {
          id: 'discrete',
          label: 'Discrete manufacturing',
          cardTheme: 'violet',
          image: {
            src: '/images/solutions/smart-manufacturing/discrete-manufacturing.png',
            alt: 'Discrete manufacturing automation',
            width: 320,
            height: 240,
          },
          bullets: [
            'Construction and building materials',
            'Automobiles',
            'Aerospace and defense',
            'Machines and equipment',
            'Medical devices',
            'Appliances',
            'Electronics',
            'Automotive parts',
          ],
        },
        {
          id: 'process',
          label: 'Process manufacturing',
          cardTheme: 'sky',
          image: {
            src: '/images/solutions/smart-manufacturing/process-manufacturing.png',
            alt: 'Process manufacturing automation',
            width: 320,
            height: 240,
          },
          bullets: [
            'Chemical',
            'Food and beverage',
            'Oil and gas',
            'Pharmaceutical',
            'Metal and steel',
            'Glass',
            'Textile',
            'Adhesives and Paint',
          ],
        },
      ],
    },
    pillars: {
      eyebrow: 'PLATFORM',
      title: 'Discover the complete smart product stack',
      subtitle: 'Connect, build, and launch with firmware, cloud, and mobile apps on one platform.',
      items: [
        {
          id: 'connect',
          label: 'Connect',
          title: 'Connect devices seamlessly',
          summary:
            'Bridge your hardware to the cloud with proven firmware SDKs, multi-protocol support, and factory-grade provisioning.',
          image: {
            src: '/images/solutions/smart-manufacturing/platform-connect-image.png',
            alt: 'Connected manufacturing devices and firmware integration',
            width: 1200,
            height: 800,
          },
          items: [
            {
              title: 'Universal firmware SDK',
              description: 'ESP32/ESP8266 libraries with MQTT, OTA, and provisioning built in.',
            },
            {
              title: 'Multi-protocol support',
              description: 'Wi-Fi, Bluetooth, and gateway integrations for diverse hardware.',
            },
            {
              title: 'Factory provisioning',
              description: 'SmartConfig, AP mode, and bulk credential tools for production lines.',
            },
            {
              title: 'Edge-ready architecture',
              description: 'Filter and act on data locally before cloud sync when needed.',
            },
          ],
        },
        {
          id: 'build',
          label: 'Build',
          title: 'Build your branded experience',
          summary:
            'Deliver a cohesive branded experience across cloud infrastructure, mobile apps, and partner dashboards.',
          image: {
            src: '/images/solutions/smart-manufacturing/platform-build-image.png',
            alt: 'Branded cloud platform and mobile apps for manufacturing',
            width: 1200,
            height: 800,
          },
          items: [
            {
              title: 'White-label cloud',
              description: 'MQTT broker, device twins, and multi-tenant management under your brand.',
            },
            {
              title: 'Mobile applications',
              description: 'Branded iOS and Android apps with your logo, colors, and flows.',
            },
            {
              title: 'Partner dashboard',
              description: 'Fleet management, user roles, and operational controls in one portal.',
            },
            {
              title: 'REST APIs',
              description: 'Integrate with ERP, CRM, and analytics tools your business already uses.',
            },
          ],
        },
        {
          id: 'launch',
          label: 'Launch',
          title: 'Launch and scale with confidence',
          summary:
            'Scale from pilot to mass production with structured QA, OTA updates, analytics, and go-to-market support.',
          image: {
            src: '/images/solutions/smart-manufacturing/platform-launch-image.png',
            alt: 'Smart manufacturing production launch and scale',
            width: 1200,
            height: 800,
          },
          items: [
            {
              title: 'Production QA',
              description: 'Structured testing from prototype through mass manufacturing.',
            },
            {
              title: 'OTA firmware updates',
              description: 'Staged rollouts with rollback support across your device fleet.',
            },
            {
              title: 'Product analytics',
              description: 'Usage insights, health monitoring, and failure diagnostics.',
            },
            {
              title: 'Go-to-market support',
              description: 'Documentation, training, and partner success through launch.',
            },
          ],
        },
      ],
    },
    useCases: {
      eyebrow: 'USE CASES',
      title: 'One platform for holistic product intelligence',
      subtitle: 'Explore how manufacturers use Aeviot across the connected product lifecycle.',
      items: [
        {
          id: 'connectivity',
          label: 'Device connectivity',
          title: 'Turn any product into a connected device',
          subtitle: 'Add Wi-Fi or Bluetooth intelligence without redesigning your hardware from scratch.',
          features: [
            {
              title: 'Rapid prototyping',
              description:
                'Validate connectivity on existing boards in weeks — test MQTT, provisioning, and cloud pairing before committing to a custom PCB redesign.',
            },
            {
              title: 'SDK integration',
              description:
                'Drop-in firmware libraries for ESP32, ESP8266, and common MCU platforms with documented APIs, sample projects, and production-ready defaults.',
            },
            {
              title: 'Provisioning flows',
              description:
                'Consumer-friendly setup via app-guided onboarding, SmartConfig, AP mode, and bulk factory tools that scale from pilot builds to mass production.',
            },
            {
              title: 'Offline resilience',
              description:
                'Graceful reconnect, local control, and queued telemetry when cloud connectivity drops — so devices stay useful on the factory floor and in the field.',
            },
            {
              title: 'Security by design',
              description:
                'Encrypted transport, device identity, and secure credential storage from day one — aligned with common IoT security baselines and audit requirements.',
            },
            {
              title: 'Certification support',
              description:
                'Guidance for FCC, CE, and regional compliance paths, plus documentation templates that accelerate radio, safety, and connectivity certifications.',
            },
          ],
        },
        {
          id: 'fleet',
          label: 'Fleet analytics',
          title: 'Visibility across your entire product fleet',
          subtitle: 'Monitor health, usage, and performance for every unit in the field.',
          features: [
            {
              title: 'Real-time telemetry',
              description:
                'Live device status, threshold alerts, and anomaly detection across every connected unit — with dashboards tuned for operations and engineering teams.',
            },
            {
              title: 'Usage patterns',
              description:
                'Understand how customers interact with your product, which features drive retention, and where firmware or UX improvements will have the most impact.',
            },
            {
              title: 'Failure diagnostics',
              description:
                'Root-cause data, crash logs, and event timelines to improve hardware reliability, reduce returns, and prioritize the next firmware release.',
            },
            {
              title: 'Geographic insights',
              description:
                'Regional performance maps, deployment density, and environmental correlation — useful for support staffing, spare-parts planning, and market expansion.',
            },
            {
              title: 'Custom dashboards',
              description:
                'KPIs tailored to your product and business model, from OEE-style metrics on the plant floor to consumer engagement scores in the field.',
            },
            {
              title: 'Export & APIs',
              description:
                'Pipe telemetry into BI tools, ERP systems, and internal data lakes via REST APIs, webhooks, and scheduled exports your team already uses.',
            },
          ],
        },
        {
          id: 'ota',
          label: 'OTA updates',
          title: 'Keep every device current in the field',
          subtitle: 'Deploy firmware improvements safely with staged rollouts and rollback.',
          features: [
            {
              title: 'Staged rollouts',
              description:
                'Release to internal test cohorts, regional pilots, or percentage-based groups before fleet-wide deployment — with pause and promote controls.',
            },
            {
              title: 'Version management',
              description:
                'Track firmware versions, hardware revisions, and configuration profiles across all deployed units from a single fleet management console.',
            },
            {
              title: 'Automatic rollback',
              description:
                'Recover quickly if an update causes issues — revert affected devices to the last known-good build without truck rolls or manual recovery.',
            },
            {
              title: 'Delta updates',
              description:
                'Minimize bandwidth and cellular cost with efficient patch delivery, ideal for large fleets and devices on constrained networks.',
            },
            {
              title: 'Scheduled maintenance',
              description:
                'Plan updates during low-usage windows, coordinate with manufacturing change orders, and notify stakeholders before maintenance windows begin.',
            },
            {
              title: 'Audit trail',
              description:
                'Full history of what was deployed, to which devices, by whom, and when — supporting quality reviews, compliance, and post-incident analysis.',
            },
          ],
        },
      ],
    },
  },
  'smart-house': {
    heroEyebrow: 'Smart Residential Solutions',
    trustedBy,
    education: {
      tabs: [
        {
          id: 'what',
          label: 'What',
          title: 'What is a smart residential platform?',
          items: [],
          blocks: [
            {
              title: 'What is a smart residential platform?',
              description:
                'A unified access, automation, and monitoring stack for multifamily and residential properties — with white-label apps partners brand as their own.',
            },
            {
              title: 'What is smart building automation?',
              description:
                'Smart building automation connects locks, sensors, climate, and lighting for proactive operations, resident experiences, and portfolio-wide visibility.',
            },
          ],
          cta: { label: 'More FAQs', to: '#faqs' },
        },
        {
          id: 'why',
          label: 'Why',
          title: 'Why property partners need smart amenities',
          items: [
            'Increase rental premiums and resident retention with premium experiences.',
            'Reduce maintenance costs with proactive sensor alerts.',
            'Differentiate properties in competitive real estate markets.',
            'Centralize access control and operations across portfolios.',
          ],
        },
        {
          id: 'how',
          label: 'How',
          title: 'How to launch with Aeviot',
          items: [
            'Partner onboarding and market assessment.',
            'White-label platform setup and hardware alignment.',
            'Resident app launch with training and ongoing support.',
          ],
          cta: partnerDemoCta,
        },
      ],
    },
    verticals: {
      eyebrow: 'PROPERTY TYPES',
      title: 'Smart living for every residential segment',
      subtitle: 'Purpose-built modules for multifamily, student housing, and premium residences.',
      items: [
        {
          id: 'multifamily',
          label: 'Multifamily',
          cardTheme: 'rose',
          image: {
            src: '/images/solutions/smart-house/multi-family-image.png',
            alt: 'Multifamily property dashboard',
            width: 800,
            height: 520,
          },
          bullets: [
            'Apartment complexes and condo buildings',
            'Build-to-rent communities',
            'Mixed-use residential towers',
            'Affordable and market-rate housing',
            'Property management portfolios',
          ],
        },
        {
          id: 'premium',
          label: 'Premium residences',
          cardTheme: 'sky',
          image: {
            src: '/images/solutions/smart-house/premium-residence-image.png',
            alt: 'Resident mobile app experience',
            width: 720,
            height: 480,
          },
          bullets: [
            'Luxury apartments and penthouses',
            'Student housing and co-living',
            'Senior living communities',
            'Single-family rental portfolios',
            'Developer show homes',
          ],
        },
      ],
      hub: {
        laptopImage: {
          src: '/images/solutions/smart-house/property-image.png',
          alt: 'Property management dashboard on laptop',
          width: 720,
          height: 480,
        },
        phoneImage: {
          src: '/images/home/platform-phone.png',
          alt: 'Resident mobile app on phone',
          width: 372,
          height: 527,
        },
        flowCards: [
          
        ],
      },
    },
    pillars: {
      eyebrow: 'PLATFORM',
      title: 'Everything partners need to run smart residential programs',
      subtitle: 'Provision, manage, and integrate connected amenities from one branded stack.',
      items: [
        {
          id: 'provision',
          label: 'Provision',
          title: 'Onboard devices at scale',
          image: {
            src: '/images/solutions/smart-house/provision.png',
            alt: 'Device provisioning dashboard',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'Bulk provisioning', description: 'SmartConfig and AP mode for fast unit turnover.' },
            { title: 'Multi-tenant setup', description: 'Separate buildings, units, and resident roles.' },
            { title: 'Hardware flexibility', description: 'Support for locks, sensors, thermostats, and more.' },
            { title: 'Vendor network', description: 'Access certified device partners and integrations.' },
          ],
        },
        {
          id: 'manage',
          label: 'Manage',
          title: 'Operate properties from one portal',
          image: {
            src: '/images/solutions/smart-house/manage.png',
            alt: 'Property management portal',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'Access control', description: 'Keyless entry and visitor management for staff and residents.' },
            { title: 'Asset protection', description: 'Leak, motion, and climate alerts for preventative maintenance.' },
            { title: 'Resident app', description: 'Branded mobile controls, scenes, and notifications.' },
            { title: 'Portfolio dashboard', description: 'Multi-site visibility for property managers.' },
          ],
        },
        {
          id: 'integrate',
          label: 'Integrate',
          title: 'Connect to your existing systems',
          image: {
            src: '/images/solutions/smart-house/integrate.png',
            alt: 'Resident app and integrations',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'PMS integrations', description: 'Sync with property management and leasing tools.' },
            { title: 'REST APIs', description: 'Custom workflows and third-party app connections.' },
            { title: 'Reporting', description: 'Usage analytics and operational insights.' },
            { title: 'White-label branding', description: 'Your logo, colors, and domain throughout.' },
          ],
        },
      ],
    },
    useCases: {
      eyebrow: 'USE CASES',
      title: 'Holistic smart living for residents and operators',
      subtitle: 'Explore how property partners deploy connected amenities.',
      items: [
        {
          id: 'access',
          label: 'Access control',
          title: 'Secure seamless access across the property',
          subtitle: 'From front door to amenities — managed through your branded app.',
          features: [
            { title: 'Smart locks', description: 'Keyless entry with time‑bound permissions, remote unlock, and controlled access for residents, staff, and vendors.' },
            { title: 'Visitor management', description: 'Issue digital passes for guests and deliveries with expiration rules, one‑time codes, and real‑time entry tracking.' },
            { title: 'Staff zones', description: 'Role‑based access for maintenance and leasing teams, with building/amenity zoning and scheduled credentials.' },
            { title: 'Audit logs', description: 'A complete history of entries, denied attempts, and credential changes — useful for compliance and incident reviews.' },
            { title: 'Mobile credentials', description: 'Residents unlock with their phone using secure credentials and can manage access for family members or roommates.' },
            { title: 'Amenity access', description: 'Gyms, pools, and common areas managed from one branded app with time windows, reservations, and staff overrides.' },
          ],
        },
        {
          id: 'protection',
          label: 'Asset protection',
          title: 'Protect property with proactive monitoring',
          subtitle: 'Sensors and automation reduce emergency maintenance and risk.',
          features: [
            { title: 'Leak detection', description: 'Detect water issues early and trigger instant notifications before damage spreads to adjacent units.' },
            { title: 'Climate monitoring', description: 'Spot HVAC failures and temperature anomalies to prevent mold risk, frozen pipes, and resident complaints.' },
            { title: 'Security sensors', description: 'Door/window and motion monitoring with configurable alert rules for vacant units and sensitive areas.' },
            { title: 'Automated alerts', description: 'Notify the right stakeholders with severity, location, and recommended next actions — via app, email, or SMS.' },
            { title: 'Maintenance tickets', description: 'Automatically create and route maintenance tasks with context, device history, and resolution tracking.' },
            { title: 'Insurance benefits', description: 'Reduce risk with documented sensor coverage and event history that supports underwriting and claims.' },
          ],
        },
        {
          id: 'resident',
          label: 'Resident experience',
          title: 'Premium in-unit automation',
          subtitle: 'Smart amenities that increase satisfaction and rental value.',
          features: [
            { title: 'Lighting scenes', description: 'Create welcome, sleep, away, and custom scenes that residents can trigger from the app or schedules.' },
            { title: 'Climate control', description: 'Comfort‑first schedules with energy‑aware automation that adapts to occupancy and resident preferences.' },
            { title: 'Voice assistants', description: 'Optional Alexa and Google integrations for hands‑free control of approved devices and scenes.' },
            { title: 'Energy insights', description: 'Give residents visibility into usage patterns and tips that encourage efficient, sustainable living.' },
            { title: 'Move-in flows', description: 'Streamlined onboarding that helps new tenants set up devices, permissions, and preferred automations in minutes.' },
            { title: 'Retention lift', description: 'A premium connected experience that improves satisfaction, supports renewal rates, and reduces churn.' },
          ],
        },
      ],
    },
  },
  'smart-hospitality': {
    heroEyebrow: 'Smart Hospitality Solutions',
    trustedBy,
    education: {
      tabs: [
        {
          id: 'what',
          label: 'What',
          title: 'What is smart hospitality technology?',
          items: [
            'Contactless guest journeys with mobile keys and in-room automation.',
            'Energy optimization that cuts costs without sacrificing comfort.',
            'Branded guest apps and staff operations on one platform.',
          ],
        },
        {
          id: 'why',
          label: 'Why',
          title: 'Why hotels invest in smart rooms',
          items: [
            'Lift guest satisfaction scores and support premium room rates.',
            'Reduce utility spend by up to 30% with occupancy automation.',
            'Streamline housekeeping and maintenance with real-time room status.',
            'Deliver a consistent brand experience across properties.',
          ],
        },
        {
          id: 'how',
          label: 'How',
          title: 'How to deploy with Aeviot',
          items: [
            'Property assessment and integration planning.',
            'White-label guest app and staff portal configuration.',
            'Pilot rollout with training and ongoing partnership.',
          ],
          cta: partnerDemoCta,
        },
      ],
    },
    verticals: {
      eyebrow: 'SMART HOSPITALITY SOLUTIONS',
      title: 'Connected Hospitality Solutions for Every Property Scale',
      subtitle:
        'From independent hotels to global chains, automate operations, enhance guest experiences, and manage every property through one intelligent IoT platform.',
      items: [
        {
          id: 'hotels',
          label: 'Hotels & Resorts',
          subtitle: 'Transform traditional hospitality into intelligent guest experiences.',
          cardTheme: 'rose',
          image: {
            src: '/images/solutions/smart-hospitality/smart-hotel-and-resorts-iamge.png',
            alt: 'Smart hotel and resort IoT platform',
            width: 720,
            height: 480,
          },
          bullets: [
            'Smart room automation',
            'Digital guest access',
            'Energy management',
            'Housekeeping automation',
            'Occupancy monitoring',
            'Property analytics',
          ],
          featureChips: ['🔑 Smart Access', '⚡ Energy Saving', '📊 Analytics', '🛎 Operations'],
        },
        {
          id: 'shortstay',
          label: 'Vacation Rentals & Serviced Apartments',
          subtitle: 'Operate multiple properties effortlessly with connected automation.',
          cardTheme: 'sky',
          image: {
            src: '/images/solutions/smart-hospitality/vacation-rentals-smart-hospitality-image.png',
            alt: 'Vacation rental smart automation platform',
            width: 720,
            height: 480,
          },
          bullets: [
            'Self check-in experiences',
            'Smart lock integration',
            'Remote device management',
            'Climate automation',
            'Cleaning alerts',
            'Multi-property monitoring',
          ],
          featureChips: ['🏡 Self Check-in', '🌡 Climate Control', '☁ Remote Management', '📱 Guest Experience'],
        },
      ],
    },
    pillars: {
      eyebrow: 'PLATFORM',
      title: 'Platform capabilities for modern hospitality',
      subtitle: 'Guest experience, energy management, and operations on one stack.',
      items: [
        {
          id: 'guest',
          label: 'Guest',
          title: 'Elevate the guest journey',
          image: {
            src: '/images/solutions/smart-hospitality/platform-guest-image.png',
            alt: 'Guest experience platform for smart hospitality',
            width: 960,
            height: 640,
          },
          items: [
            { title: 'Mobile check-in', description: 'Skip the front desk with digital arrival flows.' },
            { title: 'Digital room keys', description: 'Secure mobile keys with time-bound access.' },
            { title: 'In-room controls', description: 'Lighting, climate, and scenes from the guest app.' },
            { title: 'Personalized welcome', description: 'Automated room prep based on guest preferences.' },
          ],
        },
        {
          id: 'energy',
          label: 'Energy',
          title: 'Optimize energy without compromise',
          image: {
            src: '/images/solutions/smart-hospitality/platform-energy-image.png',
            alt: 'Energy management for smart hospitality properties',
            width: 960,
            height: 640,
          },
          items: [
            { title: 'Occupancy automation', description: 'HVAC and lighting respond to room occupancy.' },
            { title: 'Portfolio analytics', description: 'Per-property and per-room consumption views.' },
            { title: 'Demand response', description: 'Peak shaving and utility cost optimization.' },
            { title: 'Sustainability reporting', description: 'ESG metrics for corporate hospitality groups.' },
          ],
        },
        {
          id: 'ops',
          label: 'Operations',
          title: 'Empower staff with real-time visibility',
          image: {
            src: '/images/solutions/smart-hospitality/platform-operation.png',
            alt: 'Operations dashboard for smart hospitality staff',
            width: 960,
            height: 640,
          },
          items: [
            { title: 'Housekeeping status', description: 'Room readiness and cleaning workflows.' },
            { title: 'Maintenance alerts', description: 'Proactive fault detection and work orders.' },
            { title: 'Staff access zones', description: 'Role-based keys for housekeeping and engineering.' },
            { title: 'PMS integration', description: 'Sync with property management systems.' },
          ],
        },
      ],
    },
    useCases: {
      eyebrow: 'USE CASES',
      title: 'End-to-end smart hospitality mastery',
      subtitle: 'How operators deploy connected experiences across the guest lifecycle.',
      items: [
        {
          id: 'journey',
          label: 'Guest journey',
          title: 'Contactless from booking to checkout',
          subtitle: 'A seamless, branded experience at every touchpoint.',
          features: [
            {
              title: 'Pre-arrival',
              bullets: [
                'Capture room preferences, arrival time, and amenity requests before travel.',
                'Sync selections with PMS and housekeeping for a personalized arrival.',
              ],
            },
            {
              title: 'Mobile check-in',
              bullets: [
                'Contactless registration, ID verification, and digital key delivery.',
                'Shorter front-desk queues with staff approval controls.',
              ],
            },
            {
              title: 'In-stay controls',
              bullets: [
                'One app for lighting, climate, scenes, and entertainment on property.',
                'Occupancy-aware automations balance guest comfort and efficiency.',
              ],
            },
            {
              title: 'Concierge requests',
              bullets: [
                'Route housekeeping, dining, spa, and maintenance in one channel.',
                'Real-time staff alerts with room context and SLA tracking.',
              ],
            },
            {
              title: 'Express checkout',
              bullets: [
                'Review folios, settle payments, and depart in one tap.',
                'Auto-trigger room reset, energy savings, and housekeeping on departure.',
              ],
            },
            {
              title: 'Loyalty integration',
              bullets: [
                'Tie membership tiers to smart perks like scenes and late checkout.',
                'Use stay data to personalize offers and drive repeat bookings.',
              ],
            },
          ],
        },
        {
          id: 'rooms',
          label: 'Smart rooms',
          title: 'Premium in-room experiences',
          subtitle: 'Scenes and automation that delight guests and reduce energy waste.',
          features: [
            {
              title: 'Welcome scenes',
              bullets: [
                'Activate lighting, climate, and curtains when guests enter.',
                'Deliver branded arrival experiences from the first moment inside.',
              ],
            },
            {
              title: 'Sleep mode',
              bullets: [
                'One-tap routines dim lights, adjust temperature, and close shades.',
                'Help guests wind down while cutting overnight energy use.',
              ],
            },
            {
              title: 'Checkout reset',
              bullets: [
                'Return rooms to an energy-efficient default on departure.',
                'Power down devices and notify housekeeping to start turnover.',
              ],
            },
            {
              title: 'DND integration',
              bullets: [
                'Sync do-not-disturb across in-room controls, app, and housekeeping.',
                'Prevent unnecessary knocks and improve staff routing.',
              ],
            },
            {
              title: 'Entertainment',
              bullets: [
                'Integrate TVs, casting, and streaming with your guest platform.',
                'Consistent entertainment across room categories and brands.',
              ],
            },
            {
              title: 'Accessibility',
              bullets: [
                'Voice commands, simplified UI, and larger controls for all guests.',
                'Inclusive smart-room features without compromising design.',
              ],
            },
          ],
        },
        {
          id: 'sustainability',
          label: 'Sustainability',
          title: 'Energy and ESG at scale',
          subtitle: 'Measure, optimize, and report environmental impact across portfolios.',
          features: [
            {
              title: 'Occupancy sensing',
              bullets: [
                'Adjust HVAC, lighting, and plug loads when rooms are vacant.',
                'Cut utility spend without guests noticing a comfort trade-off.',
              ],
            },
            {
              title: 'Consumption dashboards',
              bullets: [
                'Real-time and historical energy use by room, floor, and property.',
                'Spot anomalies early with actionable operations visibility.',
              ],
            },
            {
              title: 'Carbon reporting',
              bullets: [
                'Portfolio-level emissions metrics from device and utility data.',
                'Consistent ESG reporting across hotels and regions.',
              ],
            },
            {
              title: 'Benchmarking',
              bullets: [
                'Compare properties, room types, and seasons to find outliers.',
                'Prioritize retrofits where savings potential is highest.',
              ],
            },
            {
              title: 'Guest engagement',
              bullets: [
                'Optional eco-feedback and green-stay tips in the guest app.',
                'Reinforce your sustainability brand during each stay.',
              ],
            },
            {
              title: 'Certification support',
              bullets: [
                'Audit-ready data for green building certification programs.',
                'Document automation impact for certifiers and investors.',
              ],
            },
          ],
        },
      ],
    },
  },
  'commercial-lighting': {
    heroEyebrow: 'SMART COMMERCIAL\nLIGHTING',
    trustedBy,
    education: {
      tabs: [
        {
          id: 'what',
          label: 'What',
          title: 'What is intelligent commercial lighting?',
          items: [
            'Networked controls for offices, industrial sites, and retail environments.',
            'Occupancy and daylight harvesting to cut energy up to 60%.',
            'Centralized management across multi-site portfolios.',
          ],
        },
        {
          id: 'why',
          label: 'Why',
          title: 'Why organizations upgrade lighting controls',
          items: [
            'Deep energy savings with automated schedules and sensors.',
            'Improve safety and productivity with responsive zone lighting.',
            'Remote maintenance and fault alerts reduce truck rolls.',
            'Data-driven insights for facilities and sustainability teams.',
          ],
        },
        {
          id: 'how',
          label: 'How',
          title: 'How to deploy commercial lighting',
          items: [
            'Site survey and fixture compatibility assessment.',
            'Platform configuration with scenes and schedules.',
            'Rollout with monitoring and ongoing partner support.',
          ],
          cta: partnerDemoCta,
        },
      ],
    },
    verticals: {
      eyebrow: 'VERTICALS',
      title: 'Lighting solutions for every commercial space',
      subtitle: 'Office, industrial, and retail deployments on one white-label platform.',
      items: [
        {
          id: 'office',
          label: 'Office & corporate',
          cardTheme: 'rose',
          image: {
            src: '/images/commercial-lighting-solutions-banner.jpg',
            alt: 'Office lighting automation',
            width: 720,
            height: 480,
          },
          bullets: [
            'Corporate campuses and headquarters',
            'Coworking and flex office spaces',
            'Government and institutional buildings',
            'Healthcare administrative facilities',
            'Educational campuses',
          ],
        },
        {
          id: 'industrial-retail',
          label: 'Industrial & retail',
          cardTheme: 'sky',
          image: {
            src: '/images/commercial-lighting-solutions-banner.jpg',
            alt: 'Industrial and retail lighting',
            width: 720,
            height: 480,
          },
          bullets: [
            'Warehouses and distribution centers',
            'Manufacturing plants and high-bay areas',
            'Retail stores and shopping centers',
            'Restaurants and hospitality venues',
            'Parking structures and outdoor areas',
          ],
        },
      ],
    },
    pillars: {
      eyebrow: 'PLATFORM',
      title: 'Commercial lighting platform capabilities',
      subtitle: 'Control, automate, and analyze lighting across your entire portfolio.',
      items: [
        {
          id: 'control',
          label: 'Control',
          title: 'Precise lighting control',
          image: {
            src: '/images/commercial-lighting-solutions-banner.jpg',
            alt: 'Lighting control',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'Zone dimming', description: 'Panel, driver, and fixture-level control.' },
            { title: 'Scene scheduling', description: 'Time, calendar, and event-based rules.' },
            { title: 'Occupancy sensors', description: 'Motion-based on/off and vacancy dimming.' },
            { title: 'Daylight harvesting', description: 'Adjust output based on ambient light levels.' },
          ],
        },
        {
          id: 'automate',
          label: 'Automate',
          title: 'Automate for efficiency and ambiance',
          image: {
            src: '/images/commercial-lighting-solutions-banner.jpg',
            alt: 'Lighting automation',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'Multi-site rules', description: 'Apply policies across buildings and regions.' },
            { title: 'Retail scenes', description: 'Dynamic color temperature for merchandising.' },
            { title: 'Safety modes', description: 'Emergency and egress lighting integration.' },
            { title: 'Demand response', description: 'Participate in utility load-shed programs.' },
          ],
        },
        {
          id: 'analyze',
          label: 'Analyze',
          title: 'Insights for facilities teams',
          image: {
            src: '/images/commercial-lighting-solutions-banner.jpg',
            alt: 'Lighting analytics',
            width: 640,
            height: 420,
          },
          items: [
            { title: 'Energy dashboards', description: 'Portfolio and site-level consumption views.' },
            { title: 'Fault detection', description: 'Alerts for failed fixtures and drivers.' },
            { title: 'Usage reports', description: 'Export data for sustainability reporting.' },
            { title: 'Remote maintenance', description: 'OTA updates and configuration changes.' },
          ],
        },
      ],
    },
    useCases: {
      eyebrow: 'USE CASES',
      title: 'Illuminate smarter across every environment',
      subtitle: 'How partners deploy intelligent lighting at scale.',
      items: [
        {
          id: 'office',
          label: 'Office automation',
          title: 'Productive workspaces with lower energy spend',
          subtitle: 'Occupancy-based lighting and scheduling for modern offices.',
          features: [
            { title: 'Desk zone control', description: 'Granular lighting for open-plan layouts.' },
            { title: 'Meeting room scenes', description: 'Presentation and collaboration modes.' },
            { title: 'After-hours policies', description: 'Automatic shutdown in unoccupied areas.' },
            { title: 'Tenant sub-metering', description: 'Allocate lighting costs per tenant.' },
            { title: 'Wellness lighting', description: 'Circadian-friendly color temperature.' },
            { title: 'BMS integration', description: 'Connect with building management systems.' },
          ],
        },
        {
          id: 'industrial',
          label: 'Industrial sites',
          title: 'Robust controls for demanding environments',
          subtitle: 'High-bay and warehouse lighting built for reliability.',
          features: [
            { title: 'High-bay dimming', description: 'Control fixtures at height safely.' },
            { title: 'Aisle activation', description: 'Light only occupied zones in warehouses.' },
            { title: 'Safety compliance', description: 'Meet industrial lighting standards.' },
            { title: 'Harsh environment', description: 'Hardware rated for plants and cold storage.' },
            { title: 'Shift scheduling', description: 'Align lighting with production shifts.' },
            { title: 'Maintenance planning', description: 'Predictive alerts before failures.' },
          ],
        },
        {
          id: 'retail',
          label: 'Retail ambiance',
          title: 'Lighting that lifts sales and brand experience',
          subtitle: 'Dynamic scenes and color control for retail and hospitality.',
          features: [
            { title: 'Merchandising scenes', description: 'Highlight products with tuned CCT.' },
            { title: 'Seasonal themes', description: 'Schedule holiday and campaign lighting.' },
            { title: 'Wayfinding', description: 'Guide shoppers with responsive zones.' },
            { title: 'Energy caps', description: 'Limit consumption during peak tariffs.' },
            { title: 'Brand consistency', description: 'Replicate scenes across store locations.' },
            { title: 'Sales correlation', description: 'Analytics tied to foot traffic patterns.' },
          ],
        },
      ],
    },
  },
};
